import { useCallback, useState } from 'react'

import { api } from 'app/api'
import { POST_ORG_GENERAL_SETTINGS_MASS } from 'app/common/api/requests/food/schools/general_settings'
import { defaultGeneralSettings } from 'app/dataProviders/org/generalSettingsOrg'

import type { GeneralSettings, SettingsKey } from 'app/dataTypes/org/settings/GeneralSettings'

type State = {
  state: boolean
  modified: boolean
}

type Actions = {
  request: () => Promise<void>
  submit: (orgIds?: Array<number>) => Promise<boolean>
  change: (v: boolean) => void
}

export function useSingleGeneralSettingsOption({
  orgId,
  optionKey,
  error,
  requestGeneralSettings,
}: {
  orgId: number
  optionKey:
    | SettingsKey.camp_subsidy_orders_use_age_at_period_start
    | SettingsKey.disable_edit_reject_cafeteria_subsidies_period
  error: string
  requestGeneralSettings: () => Promise<GeneralSettings | null>
}): [State, Actions] {
  const [state, setState] = useState<boolean>(defaultGeneralSettings[optionKey])
  const [modified, setModified] = useState(false)

  const request = useCallback(async () => {
    const res = await requestGeneralSettings()
    if (res) {
      const value = res[optionKey]
      if (typeof value === 'boolean') {
        setState(value)
        setModified(false)
      }
    }
  }, [optionKey, requestGeneralSettings])

  const submit = useCallback(
    async (overrideOrgIds?: Array<number>) => {
      const orgIds = overrideOrgIds || [orgId]
      const res = await api.request(POST_ORG_GENERAL_SETTINGS_MASS, {
        error,
        params: {
          schools_ids: orgIds.map(String),
          settings: {
            [optionKey]: state,
          },
        },
      })

      if (res && overrideOrgIds && !overrideOrgIds.find((id) => id === orgId)) {
        // были изменены данные другой организации, не влияет на текущую форму
        return true
      }

      if (res) {
        setModified(false)
      }
      return !!res
    },
    [orgId, optionKey, error, state],
  )

  const change = useCallback((newValue: boolean) => {
    setModified(true)
    setState(newValue)
  }, [])

  return [
    {
      state,
      modified,
    },
    {
      request,
      submit,
      change,
    },
  ]
}
